import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';

// --- Interfaces ---
interface Advisor {
  AdvisorId: number;
  AdvisorName: string;
  IsAllowedToSee: boolean;
  WealthId: number;
  WealthAccountId: number;
  IsClosed: boolean;
  Name: string;
}

interface AccountPermission {
  IsAllowedToSee: boolean;
  Advisors: Advisor[];
  AdvisorsCount: number;
  AdvisorsWhoCanSee: number;
  [key: string]: any;
}

interface DefaultControl {
  DefaultIsFullControl: boolean;
  [key: string]: any;
}

type AdvisorPermissionPayload = Advisor;

// --- API Slice Definition ---
export const permissionsApi = createApi({
  reducerPath: 'permissionsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AccountPermissions', 'DefaultControl'],
  endpoints: (builder) => ({
    getAccountPermissions: builder.query<
      AccountPermission,
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: `/api/investor/Accounts/${accountId}/Permissions`,
        method: 'GET',
      }),
      providesTags: (result, error, { accountId }) => [
        { type: 'AccountPermissions', id: accountId },
      ],
    }),

    getDefaultFullControl: builder.query<DefaultControl, void>({
      query: () => ({
        url: '/api/investor/defaultFullControl',
        method: 'GET',
      }),
      providesTags: ['DefaultControl'],
    }),

    updateWealthPermissions: builder.mutation<
      any,
      Partial<AccountPermission> & { accountId: string }
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const { accountId, ...body } = arg;
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const result = await baseQuery({
          url: `/api/${currentRole}/wealthPermissions`,
          method: 'PUT',
          body,
        });
        return result as { data: any };
      },
      async onQueryStarted(
        { accountId, ...payload },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          permissionsApi.util.updateQueryData(
            'getAccountPermissions',
            { accountId },
            (draft) => {
              // Optimistically update the cache with the state the user intended.
              Object.assign(draft, payload);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      // DO NOT invalidate tags here. We are preventing the automatic refetch
      // that was bringing back the bad data from the server.
    }),

    updateDefaultFullControl: builder.mutation<
      any,
      { DefaultIsFullControl: boolean }
    >({
      queryFn: async (payload, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const result = await baseQuery({
          url: `/api/${currentRole}/defaultFullControl`,
          method: 'PUT',
          body: payload,
        });
        return result as { data: any };
      },
    }),

    updateIndividualAdvisorPermission: builder.mutation<
      any,
      {
        accountId: string;
        advisorId: number;
        payload: AdvisorPermissionPayload;
      }
    >({
      queryFn: async (
        { accountId, advisorId, payload },
        { getState },
        _extra,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const result = await baseQuery({
          url: `/api/investor/Accounts/${accountId}/Permissions/${advisorId}`,
          method: 'PUT',
          body: payload,
        });
        return result as { data: any };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'AccountPermissions', id: arg.accountId },
      ],
    }),
  }),
});

export const {
  useGetAccountPermissionsQuery,
  useGetDefaultFullControlQuery,
  useUpdateWealthPermissionsMutation,
  useUpdateDefaultFullControlMutation,
  useUpdateIndividualAdvisorPermissionMutation,
} = permissionsApi;
